import { useEffect, useState } from "react";
import { Croissant, ArrowUpRight, LogOut, User } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { goToSection } from "@/lib/scroll";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { label: "Manifesto", href: "#manifesto" },
  { label: "Menu", href: "#menu" },
  { label: "Order", href: "#order" },
  { label: "Outlets", href: "#outlets" },
  { label: "Reviews", href: "#reviews" },
];

export const SiteNav = () => {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, signOut, setAuthModal } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => (e) => {
    e.preventDefault();
    goToSection(navigate, pathname, href);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#FDFBF7]/85 backdrop-blur-md border-b border-[rgba(28,19,14,0.08)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-12 h-16 lg:h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo-link" onClick={go("#top")} className="flex items-center gap-2.5">
          <span className="grid place-items-center w-9 h-9 rounded-full bg-[#1C130E] text-[#FDFBF7]">
            <Croissant size={16} strokeWidth={1.5} />
          </span>
          <span className="font-display text-2xl font-semibold tracking-tight">French Pie</span>
        </a>
        <div className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}-link`}
              onClick={go(l.href)}
              className="text-sm font-medium text-[#1C130E]/70 hover:text-[#C85A32] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <a
            href="#contact"
            data-testid="nav-franchise-btn"
            onClick={go("#contact")}
            className="group hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[#1C130E] text-[#FDFBF7] text-sm font-medium pl-5 pr-4 py-2.5 hover:bg-[#C85A32] transition-colors"
          >
            Franchise Enquiry
            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:rotate-45" />
          </a>
          {user ? (
            <div className="flex items-center gap-2" data-testid="nav-user-chip">
              <a
                href="/my-orders"
                data-testid="nav-my-orders-link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/my-orders");
                }}
                className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(28,19,14,0.15)] px-4 py-2.5 text-sm font-medium hover:border-[#C85A32] hover:text-[#C85A32] transition-colors"
              >
                <User size={14} />
                <span>{user.name.trim().split(" ")[0]}</span>
              </a>
              <button
                onClick={signOut}
                data-testid="nav-signout-btn"
                aria-label="Sign out"
                className="grid place-items-center w-9 h-9 rounded-full border border-[rgba(28,19,14,0.15)] hover:border-[#C85A32] hover:text-[#C85A32] transition-colors"
              >
                <LogOut size={14} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setAuthModal("signin")}
              data-testid="nav-signin-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(28,19,14,0.15)] px-4 py-2.5 text-sm font-medium hover:border-[#C85A32] hover:text-[#C85A32] transition-colors"
            >
              Sign in
            </button>
          )}
        </div>
      </nav>
    </header>
  );
};
